/**
 * Which renderer draws this character.
 *
 * One per animal, not one per species-with-parameters. A capybara reads from a
 * head wider than tall with a blunt square muzzle; a unicorn reads from the
 * opposite — long, narrow, horned. Parameterising one rig over both would
 * produce the costume both rigs exist to avoid.
 */
export type Rig = 'human' | 'creature' | 'unicorn';

/**
 * One character's appearance.
 *
 * Here rather than in the `characters` table on purpose: a renderer knob and
 * the renderer change together, so a column per knob would mean a migration
 * every time the face gains an eyebrow. `art_key` is the join, and
 * `client/tools/avatar_contract.mjs` fails the build if the two sides drift.
 */
export type Face = {
  rig: Rig;
  /** Silhouette first (§8.3). Read at 38px before anything else is legible. */
  hair: 'short' | 'bob' | 'long' | 'curls' | 'buzz' | 'tie' | 'ears' | 'mane';
  /** Head width as a fraction of the 100-unit viewBox. */
  headWidth: number;
  skin: string;
  hairColor: string;
  /** Eye aspect: 1 round, below 1 narrow. */
  eye: number;
};

export const FACES: Record<string, Face> = {
  // Kid characters: rounder heads, larger eyes.
  mia: {
    rig: 'human',
    hair: 'curls',
    headWidth: 58,
    skin: '#F1C8A4',
    hairColor: '#4A2C17',
    eye: 1.1,
  },
  bo: {
    rig: 'human',
    hair: 'buzz',
    headWidth: 56,
    skin: '#8D5524',
    hairColor: '#1B1210',
    eye: 1.05,
  },
  pip: {
    rig: 'human',
    hair: 'bob',
    headWidth: 54,
    skin: '#FADCC0',
    hairColor: '#C98A3A',
    eye: 1.15,
  },
  cappy: {
    rig: 'creature',
    hair: 'ears',
    headWidth: 64,
    skin: '#A9764B',
    hairColor: '#6B4423',
    eye: 0.9,
  },
  // The narrowest head in the cast, because a horse's is: this rig draws the
  // skull and the muzzle as one tapering shape, so the width here is the
  // crown's and everything below it is narrower. `hairColor` is the mane, and
  // the horn is the rig's own constant — a unicorn in a second horn colour
  // would be a different character, not a colourway.
  uni: {
    rig: 'unicorn',
    hair: 'mane',
    headWidth: 44,
    skin: '#FBCFE8',
    hairColor: '#F472B6',
    eye: 1.2,
  },
  // Adults: narrower heads, narrower eyes.
  ava: {
    rig: 'human',
    hair: 'long',
    headWidth: 52,
    skin: '#EFC3A0',
    hairColor: '#7A4B2A',
    eye: 0.95,
  },
  ella: {
    rig: 'human',
    hair: 'tie',
    headWidth: 50,
    skin: '#F3D2B3',
    hairColor: '#2E2A26',
    eye: 0.9,
  },
  marcus: {
    rig: 'human',
    hair: 'short',
    headWidth: 55,
    skin: '#6B4331',
    hairColor: '#20160F',
    eye: 0.9,
  },
  nina: {
    rig: 'human',
    hair: 'bob',
    headWidth: 51,
    skin: '#E8B98E',
    hairColor: '#A32E2E',
    eye: 1.0,
  },
};

/** The human default is wrong for an unknown character, and survivable. */
const FALLBACK: Face = FACES.ava;

export function faceFor(artKey: string): Face {
  return FACES[artKey] ?? FALLBACK;
}
